import { Box, CircularProgress } from "@mui/material";
import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  deleteUser,
  getAccountUser,
  updateUser,
  updateUserPassword,
} from "../../../api/users";
import ManageUserForm from "../../../components/Users/ManageUserForm";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { setLoading } from "../../../store/UI";
import { Role } from "../../../types/interfaces/account";
import { PrivateSettings } from "../../../types/interfaces/app";
import {
  UpdateUserPasswordRequest,
  UpdateUserRequest,
  User,
} from "../../../types/model/user";
import { StoreInterfaces } from "../../../types/store";

const ManageUser = () => {
  const { accountId, userId } = useParams<{
    accountId: string;
    userId: string;
  }>();
  const dispatch = useDispatch();

  const [user, setUser] = React.useState<User | undefined>(undefined);
  const [loadingState, setLoadingState] = React.useState(true);
  const [availableRoles, setAvailableRoles] = React.useState<Role[]>([]);
  const handleError = useErrorHandler();

  const privateSettings: PrivateSettings | undefined = useSelector(
    (state: StoreInterfaces) => state.app.privateSettings,
    shallowEqual,
  );

  useEffect(() => {
    const fetchUser = async () => {
      if (accountId && userId) {
        setLoadingState(true);
        dispatch(setLoading(true));
        try {
          const fetchedUser = await getAccountUser(
            Number.parseInt(accountId),
            Number.parseInt(userId),
          );
          setUser(fetchedUser);
        } catch (error) {
          handleError(error);
        } finally {
          setLoadingState(false);
          dispatch(setLoading(false));
        }
      }
    };

    fetchUser();
  }, [accountId, userId, dispatch]);

  useEffect(() => {
    if (privateSettings?.roles) {
      setAvailableRoles(privateSettings.roles);
    }
  }, [privateSettings]);

  const handleUpdate = async (values: UpdateUserRequest): Promise<boolean> => {
    if (!user) return false;

    dispatch(setLoading(true));

    try {
      await updateUser(
        Number.parseInt(accountId!),
        Number.parseInt(userId!),
        values,
      );

      const fetchedUser = await getAccountUser(
        Number.parseInt(accountId!),
        Number.parseInt(userId!),
      );

      setUser(fetchedUser);
      return true;

    } catch (error) {
      handleError(error);
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpdatePassword = async (
    values: UpdateUserPasswordRequest
  ): Promise<void> => {

    if (!user) return;
    dispatch(setLoading(true));

    try {
      await updateUserPassword(
        Number.parseInt(accountId!),
        Number.parseInt(userId!),
        { password: values.password }
      );

    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!user) return;

    dispatch(setLoading(true));
    try {
      await deleteUser(
        Number.parseInt(accountId!),
        Number.parseInt(userId!)
      );

    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (loadingState || !user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ManageUserForm
        userData={user}
        onUpdate={handleUpdate}
        onUpdatePassword={handleUpdatePassword}
        onDelete={handleDelete}
        availableRoles={availableRoles}
        isActive={user.active}
      />
    </Box>
  );
};

export default ManageUser;
